import { useEffect } from "react";
import { useDarkMode } from "../../../../../context/ThemeContext";
import CustomAlert from "../../../../../components/base/CustomAlert";
import { useLicenseForm } from "./hooks";
import {
  LicenseHeader,
  LicenseList,
  LicensePagination,
  LicenseDetailsModal,
  LicenseAddModal,
} from "./components";
import type { ThemeClasses } from "./types";

export default function LicensesSection() {
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const {
    licenses,
    meta,
    loading,
    selectedLicense,
    alert,
    isModalOpen,
    modalType,
    showAddModal,
    loadingSubmit,
    isEdit,
    editLicenseId,
    form,
    selectedStatus,
    searchQuery,
    selectedView,
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleEditClick,
    handleAddClick,
    handleSubmitLicense,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeAddModal,
    hasFormChanges,
    handleDeleteLicense,
    currentPage,
  } = useLicenseForm();

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const totalPages = meta?.totalPages || 1;

  return (
    <div className="h-full flex flex-col">
      {alert.show && (
        <CustomAlert
          title={alert.title}
          text={alert.message}
          icon={alert.type}
        />
      )}

      <LicenseHeader
        themeClasses={themeClasses}
        selectedStatus={selectedStatus}
        searchQuery={searchQuery}
        selectedView={selectedView}
        onStatusChange={handleStatusChange}
        onSearchChange={handleSearchChange}
        onViewChange={setSelectedView}
        onAddLicense={handleAddClick}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            <LicenseList
              licenses={licenses}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              selectedView={selectedView}
              onDetails={handleDetailsClick}
              onEdit={handleEditClick}
              onDelete={handleDeleteLicense}
            />
            {licenses.length > 0 && (
              <LicensePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {isModalOpen && modalType === "details" && selectedLicense && (
        <LicenseDetailsModal
          license={selectedLicense}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={closeModal}
          onEdit={handleEditClick}
          onDelete={handleDeleteLicense}
        />
      )}

      {showAddModal && (
        <LicenseAddModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          form={form}
          loadingSubmit={loadingSubmit}
          onClose={closeAddModal}
          onSubmit={handleSubmitLicense}
          isEdit={isEdit}
          hasFormChanges={hasFormChanges}
          editLicenseId={editLicenseId}
        />
      )}
    </div>
  );
}
